import { put, select } from 'redux-saga/effects';
import { selectFormsObject } from '../formControlSelectors';
import { formControlActions } from '../formControlSlice';

/**
 * Latest agent copy per form, shared by every copy / un-copy handler.
 *
 * Copies run in parallel (`takeEvery`) and some wait for the backend, so a slow response for a row the
 * agent has already replaced, un-copied or overridden with a cell / announcement copy must not be applied
 * over the newer selection. A handler starts its copy with `beginCopy`, an async one checks `dropIfStale`
 * after its request, and every one ends it with `endCopy` from a `finally`.
 *
 * The latest copy is stored on the form (`latestCopy`), so a form that closes or is re-created under the
 * same id drops it, and a response still loading for it is dropped too. Ids are never reused.
 */
const selectLatestCopy = (transactionId) => (state) => selectFormsObject(state)?.[transactionId]?.latestCopy;

const isSameRow = (a, b) => !!a && !!b?.tableName && b.key != null && a.tableName === b.tableName && String(a.key) === String(b.key);

/** `source` ({ tableName, key }) names the table row the copy comes from. Returns null when the form is not open. */
export function* beginCopy(transactionId, source = null) {
    yield put(formControlActions.beginCopy({ formId: transactionId, source }));
    const latestCopy = yield select(selectLatestCopy(transactionId));
    return latestCopy?.id ?? null;
}

/**
 * A cell copy from the row a full-row copy is still loading for uses the same data, so it leaves that copy
 * alone and returns null; otherwise it starts a copy of its own.
 */
export function* beginCellCopy(transactionId, source) {
    const latestCopy = yield select(selectLatestCopy(transactionId));
    if (isSameRow(latestCopy?.source, source)) return null;
    return yield* beginCopy(transactionId, source);
}

export function* endCopy(transactionId, copyId) {
    if (copyId == null) return;
    yield put(formControlActions.endCopy({ formId: transactionId, copyId }));
}

/**
 * Whether a newer copy started (or the form closed) while this one was loading. A stale row copy is
 * recorded as dropped, so its table clears the row highlight.
 */
export function* dropIfStale(transactionId, copyId, source) {
    const latestCopy = yield select(selectLatestCopy(transactionId));
    if (copyId != null && latestCopy?.id === copyId) return false;
    if (source) yield put(formControlActions.setDroppedCopy({ formId: transactionId, tableName: source.tableName, key: source.key }));
    return true;
}
